import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import {
  useBookingsList,
  useBookingsStatusUpdate,
  useBlacklistBooking,
} from "src/data/booking/queries/booking";
import { Booking, BookingStatus } from "src/business/booking";
import { colorTextMuted } from "src/design/tokens";
import { SnackBar } from "src/design/SnackBar";
import { TipsCard } from "src/design/TipsCard";
import { BookingStatsRow } from "./components/BookingStatsRow";
import {
  BookingSearchBar,
  DateFilter,
  StatusFilter,
} from "./components/BookingSearchBar";
import { BookingList } from "./components/BookingList";
import { BlacklistOfferDialog } from "./components/BlacklistOfferDialog";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import { BookingScope, resolveBookingScope } from "src/business/bookingScope";

const resolveRange = (
  filter: DateFilter,
  customStart: Dayjs,
  customEnd: Dayjs,
): { startDate: number; endDate: number } => {
  switch (filter) {
    case "today":
      return {
        startDate: dayjs().startOf("day").valueOf(),
        endDate: dayjs().endOf("day").valueOf(),
      };
    case "upcoming":
      return {
        startDate: dayjs().startOf("day").valueOf(),
        endDate: dayjs().add(60, "day").endOf("day").valueOf(),
      };
    case "custom":
      return {
        startDate: customStart.startOf("day").valueOf(),
        endDate: customEnd.endOf("day").valueOf(),
      };
    default: // this_week
      return {
        startDate: dayjs().startOf("week").valueOf(),
        endDate: dayjs().endOf("week").valueOf(),
      };
  }
};

const BookingsPage = ({ restaurant_id }: { restaurant_id?: string }) => {
  const { restaurantId: restaurantIdFromParams } = useParams<{
    restaurantId: string;
  }>();
  const resolvedRestaurantId = restaurant_id || restaurantIdFromParams || "";
  const { t } = useTranslation();

  const { user } = useContext(UserContext);
  const { restaurants } = useContext(RestaurantContext);

  const selectedRestaurant = useMemo(() => {
    return restaurants.find((r) => r.id === resolvedRestaurantId);
  }, [restaurants, resolvedRestaurantId]);
  const restaurantCapabilities = useMemo(() => {
    return selectedRestaurant?.capabilities.map((cap) => cap.name);
  }, [selectedRestaurant]);

  // Which slice of the booking module was bought. `visibilite` sells today's
  // service only, so that restaurant gets this page pinned to today rather than
  // a locked one — the day's bookings are exactly what it pays for.
  const scope = resolveBookingScope(restaurantCapabilities, user);
  const sameDayOnly = scope === BookingScope.SameDay;
  const bookingFeature = sameDayOnly
    ? "same_day_booking_management"
    : "booking_management";

  const isBookingUpdateEnabled = isFeatureSectionEnabled(
    ["booking_update"],
    bookingFeature,
    user,
    restaurantCapabilities,
  );

  // Mirrors the backend permission gate: only staff holding the blacklist
  // permission are ever offered the blacklist dialog, even when the backend
  // reports `blacklistOffered`.
  const isBlacklistEnabled = isFeatureSectionEnabled(
    ["booking_blacklist"],
    "booking_management",
    user,
    restaurantCapabilities,
  );

  const [dateFilter, setDateFilter] = useState<DateFilter>("upcoming");
  const [customStart, setCustomStart] = useState<Dayjs>(
    dayjs().startOf("week"),
  );
  const [customEnd, setCustomEnd] = useState<Dayjs>(dayjs().endOf("week"));
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [statusErrorOpen, setStatusErrorOpen] = useState(false);
  // The booking whose no-show just triggered a blacklist offer; drives the
  // confirmation dialog. `null` when no offer is pending.
  const [blacklistTarget, setBlacklistTarget] = useState<Booking | null>(null);

  // Derived rather than forced into state: the capabilities arrive with the
  // restaurant list, a render or two after the filter's initial value, and a
  // stored filter would have to be corrected after the fact.
  const effectiveDateFilter: DateFilter = sameDayOnly ? "today" : dateFilter;

  const { startDate, endDate } = useMemo(
    () => resolveRange(effectiveDateFilter, customStart, customEnd),
    [effectiveDateFilter, customStart, customEnd],
  );

  const { data: bookings = [], refetch } = useBookingsList({
    restaurantId: resolvedRestaurantId,
    startDate,
    endDate,
  });

  const { mutate: updateBookingStatus } = useBookingsStatusUpdate(
    resolvedRestaurantId,
    refetch,
  );

  const { mutate: blacklistBooking, isPending: isBlacklisting } =
    useBlacklistBooking(resolvedRestaurantId, () => {
      setBlacklistTarget(null);
      refetch();
    });

  // Transition-aware: the list item only offers legal next statuses (see
  // getAllowedBookingTransitions). A transition the backend still rejects (race)
  // surfaces an error; we don't optimistically mutate, so the displayed status
  // is left unchanged and only refreshed via refetch on success. When flagging a
  // no-show, an authorized user is offered the blacklist dialog if the backend
  // reports the booker is over the repeat-offender threshold.
  const handleStatusChange = (booking: Booking, status: BookingStatus) => {
    updateBookingStatus(
      {
        bookingID: booking.id,
        status,
        isAnonymous: booking.isAnonymous,
      },
      {
        onSuccess: ({ blacklistOffered }) => {
          if (
            status === BookingStatus.NO_SHOW &&
            blacklistOffered &&
            isBlacklistEnabled
          ) {
            setBlacklistTarget(booking);
          }
        },
        onError: () => setStatusErrorOpen(true),
      },
    );
  };

  const handleBlacklistConfirm = () => {
    if (!blacklistTarget) return;
    blacklistBooking({
      bookingID: blacklistTarget.id,
      isAnonymous: blacklistTarget.isAnonymous,
    });
  };

  const handleCustomRangeChange = (start: Dayjs, end: Dayjs) => {
    setCustomStart(start);
    setCustomEnd(end);
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3, width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "24px", lineHeight: 1.3 }}>
          {t("bookings.pageTitle")}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: colorTextMuted, mt: 0.5 }}>
          {t("bookings.pageSubtitle")}
        </Typography>
      </Box>

      <BookingStatsRow bookings={bookings} />

      <BookingSearchBar
        search={search}
        onSearchChange={setSearch}
        dateFilter={effectiveDateFilter}
        onDateFilterChange={setDateFilter}
        sameDayOnly={sameDayOnly}
        customStart={customStart}
        customEnd={customEnd}
        onCustomRangeChange={handleCustomRangeChange}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />

      <BookingList
        isBookingUpdateEnabled={isBookingUpdateEnabled}
        bookings={bookings}
        search={search}
        statusFilter={statusFilter}
        onStatusChange={handleStatusChange}
        restaurantName={selectedRestaurant?.name ?? ""}
      />

      <Box sx={{ mt: 3 }}>
        <TipsCard
          title={t("bookings.tips.title")}
          tips={[
            t("bookings.tips.responseTime"),
            t("bookings.tips.notes"),
            // The blacklist ships with full booking management; suggesting it
            // to a same-day restaurant would point at a feature it has not got.
            ...(sameDayOnly ? [] : [t("bookings.tips.blacklist")]),
          ]}
        />
      </Box>

      <SnackBar
        open={statusErrorOpen}
        onClose={() => setStatusErrorOpen(false)}
        message={t("bookings.status.updateError")}
        severity="error"
      />

      <BlacklistOfferDialog
        open={!!blacklistTarget}
        noShowCount={blacklistTarget?.noShowCount ?? 0}
        loading={isBlacklisting}
        onConfirm={handleBlacklistConfirm}
        onClose={() => setBlacklistTarget(null)}
      />
    </Box>
  );
};

export default BookingsPage;
